import React, { useState, useEffect } from 'react';
import { useTranslate } from '../lib/i18n';

// Sanitization rules that define patterns and their replacements
const SANITIZATION_RULES = [
  {
    id: 'numbers',
    // Updated pattern to only match sequences of 4 or more digits
    // - Matches 4+ digit sequences with optional separators
    // - Allows spaces, dashes, plus signs between numbers
    // - Handles both positive and negative numbers
    pattern: /\b\d{4,}(?:[.,\s\-+/*]?\d+)*\b|\b-\d{4,}(?:[.,\s\-+/*]?\d+)*\b/g,
    replacement: '[NUMERO]',
  },
];

interface Props {
  text: string;
}

// Component for displaying messages with highlighted patterns
const MessageDisplay = ({ text }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const t = useTranslate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (isTouching && !target.closest('.number-highlight')) {
        setShowTooltip(false);
        setIsTouching(false);
      }
    };

    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isTouching]);

  const splitTextWithMatches = (text: string) => {
    const segments = [];
    let lastIndex = 0;

    const pattern = SANITIZATION_RULES.find((rule) => rule.id === 'numbers')!.pattern;
    pattern.lastIndex = 0; // Reset regex state

    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      segments.push({
        type: 'match',
        content: match[0],
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return segments;
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    event.preventDefault();
    setShowTooltip(true);
    setIsTouching(true);
  };

  const handleMouseEnter = () => {
    if (!isTouching) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouching) {
      setShowTooltip(false);
    }
  };

  let segments;
  if (text && text !== '') {
    segments = splitTextWithMatches(text);
  }

  return (
    <span className="relative inline">
      {segments &&
        segments.map((segment, index) =>
          segment.type === 'match' ? (
            <span
              key={index}
              className="number-highlight group relative cursor-help text-primary line-through focus:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-1"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowTooltip(true);
                }
              }}
              onFocus={handleMouseEnter}
              onBlur={handleMouseLeave}
            >
              {segment.content}
            </span>
          ) : (
            <span key={index}>{segment.content}</span>
          )
        )}
      {showTooltip && (
        <div
          className="absolute z-50 w-64 rounded bg-popover px-2 py-1 text-xs text-popover-foreground shadow-lg"
          style={{
            left: '50%',
            transform: 'translateX(-50%) translateY(-100%)',
            top: '-8px',
          }}
        >
          {t('messageDisplay.privacyTooltip')}
        </div>
      )}
    </span>
  );
};

// Utility function for sanitizing messages before sending to backend
const sanitizeMessage = (text: string) => {
  let sanitizedText = text;
  SANITIZATION_RULES.forEach((rule) => {
    rule.pattern.lastIndex = 0; // Reset regex state
    sanitizedText = sanitizedText.replace(rule.pattern, rule.replacement);
  });
  return sanitizedText;
};

export { MessageDisplay, sanitizeMessage };
